import { useState } from "react";
import { createInstances } from "../../api/fleet";
import { createKiroApiKey, recommendUsers } from "../../api/users";
import type {
  CreateFleetInstanceRequest,
  CreateFleetInstanceResultItem,
  FleetRegion,
  RecommendUserCandidateDto,
} from "../../api/types";

const REGIONS: { value: FleetRegion; label: string }[] = [
  { value: "ap-northeast-1", label: "东京(ap-northeast-1)" },
  { value: "ap-southeast-1", label: "新加坡(ap-southeast-1)" },
];

const TIERS = ["pro", "proplus", "promax", "power"];

const MAX_KEYS_PER_RECORD = 50;
const MAX_BATCH_RECORDS = 500;
const MAX_NAME_NOTE_LENGTH = 100;

interface KeyEntryDraft {
  key: string;
  email: string;
}

/** 场景1：推荐用户条目，只需 email + account_id，key 由系统自动创建 */
interface AutoCreateEntry {
  account_id: string;
  email: string;
  username: string;
}

/**
 * keyMode: "auto" = 场景1推荐用户自动创建ksk_，"manual" = 场景2手动录入ksk_
 * autoCreateEntries: 场景1下选定的推荐用户列表
 * keyEntries: 场景2下手动录入的 key+email 列表
 */
interface RecordDraft {
  keyMode: "auto" | "manual";
  autoCreateEntries: AutoCreateEntry[];
  keyEntries: KeyEntryDraft[];
  assignedUserEmail: string;
  expiresAt: string; // YYYY-MM-DD
  nameNote: string;
  region: FleetRegion;
}

function emptyKeyEntry(): KeyEntryDraft {
  return { key: "", email: "" };
}

function emptyRecordDraft(): RecordDraft {
  return {
    keyMode: "auto",
    autoCreateEntries: [],
    keyEntries: [emptyKeyEntry()],
    assignedUserEmail: "",
    expiresAt: "",
    nameNote: "",
    region: "ap-northeast-1",
  };
}

/** 简单邮箱格式校验,与 backend validators.validate_assigned_user_email 的规则对齐(Requirement 1.5)。 */
function isValidEmail(email: string): boolean {
  if (!email || email.length > 254) return false;
  const at = email.indexOf("@");
  if (at <= 0 || at !== email.lastIndexOf("@")) return false;
  return at < email.length - 1;
}

/** 将草稿字段转换为提交请求体,并对必填字段做前端校验,返回校验错误列表(为空表示通过)。 */
function validateDraft(draft: RecordDraft): string[] {
  const errors: string[] = [];

  if (draft.keyMode === "auto") {
    // 场景1：推荐用户模式，只需至少选一个推荐用户
    if (draft.autoCreateEntries.length === 0) {
      errors.push("请至少选择 1 个推荐 Kiro 用户(点击【使用】添加)");
    } else if (draft.autoCreateEntries.length > MAX_KEYS_PER_RECORD) {
      errors.push(`推荐用户数量不能超过 ${MAX_KEYS_PER_RECORD} 个`);
    }
  } else {
    // 场景2：手动录入模式
    const nonEmptyEntries = draft.keyEntries.filter((e) => e.key.trim().length > 0);
    if (nonEmptyEntries.length === 0) {
      errors.push("请至少录入 1 个 Kiro_API_Key");
    } else if (nonEmptyEntries.length > MAX_KEYS_PER_RECORD) {
      errors.push(`Kiro_API_Key 数量不能超过 ${MAX_KEYS_PER_RECORD} 个`);
    } else {
      nonEmptyEntries.forEach((entry, idx) => {
        const key = entry.key.trim();
        if (!key.startsWith("ksk_")) {
          errors.push(`第 ${idx + 1} 个 Kiro_API_Key 格式不合法(必须以 ksk_ 为前缀)`);
        }
        const email = entry.email.trim();
        if (!email) {
          errors.push(`第 ${idx + 1} 个 Kiro_API_Key 缺少关联的 Kiro 用户邮箱`);
        } else if (!isValidEmail(email)) {
          errors.push(`第 ${idx + 1} 个 Kiro_API_Key 关联的邮箱格式不合法`);
        }
      });
    }
  }

  if (!draft.assignedUserEmail) {
    errors.push("请填写 Assigned_User_Email");
  } else if (!isValidEmail(draft.assignedUserEmail)) {
    errors.push("Assigned_User_Email 格式不合法");
  }

  if (!draft.expiresAt) {
    errors.push("请选择到期时间");
  } else {
    const expiryDate = new Date(`${draft.expiresAt}T00:00:00Z`);
    if (expiryDate.getTime() <= Date.now()) {
      errors.push("到期时间必须晚于当前时间");
    }
  }

  if (draft.nameNote.length > MAX_NAME_NOTE_LENGTH) {
    errors.push(`名称/备注长度不能超过 ${MAX_NAME_NOTE_LENGTH} 个字符`);
  }

  return errors;
}

/**
 * 场景1：为 draft 中的所有 autoCreateEntries 调用后端 Playwright 自动创建 ksk_。
 * 返回 { keys: FleetApiKeyEntry[], errors: string[] }
 * errors 非空时表示有用户创建失败，调用方决定是否中止。
 */
async function autoCreateKeysForDraft(
  entries: AutoCreateEntry[]
): Promise<{ keys: { key: string; kiro_user_email: string }[]; errors: string[] }> {
  const keys: { key: string; kiro_user_email: string }[] = [];
  const errors: string[] = [];
  for (const entry of entries) {
    try {
      const resp = await createKiroApiKey(entry.email, entry.account_id);
      if (resp.status === "SUCCESS" && resp.ksk_key) {
        keys.push({ key: resp.ksk_key, kiro_user_email: entry.email });
      } else {
        errors.push(
          `${entry.email} 自动创建 Key 失败(NEEDS_MANUAL)：${resp.error_detail ?? "未知错误"}`
        );
      }
    } catch (err) {
      errors.push(
        `${entry.email} 自动创建 Key 出错：${err instanceof Error ? err.message : String(err)}`
      );
    }
  }
  return { keys, errors };
}

function draftToRequest(
  draft: RecordDraft,
  resolvedKeys?: { key: string; kiro_user_email: string }[]
): CreateFleetInstanceRequest {
  // 场景1传入 resolvedKeys，场景2从 keyEntries 读取
  const entries =
    resolvedKeys ??
    draft.keyEntries
      .filter((e) => e.key.trim().length > 0)
      .map((e) => ({ key: e.key.trim(), kiro_user_email: e.email.trim() }));
  return {
    kiro_api_keys: entries,
    assigned_user_email: draft.assignedUserEmail,
    expires_at: draft.expiresAt,
    name_note: draft.nameNote || undefined,
    region: draft.region,
  };
}

/**
 * Kiro_API_Key 录入区域，支持两种场景：
 * - 场景1 (auto)：推荐用户模式，选择推荐用户，提交时系统自动通过 Playwright 创建 ksk_
 * - 场景2 (manual)：手动录入 ksk_ + 关联邮箱
 */
function KeyEntriesEditor({
  draft,
  onChangeDraft,
  testIdPrefix,
}: {
  draft: RecordDraft;
  onChangeDraft: (patch: Partial<RecordDraft>) => void;
  testIdPrefix: string;
}) {
  const [tier, setTier] = useState("");
  const [candidates, setCandidates] = useState<RecommendUserCandidateDto[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidateError, setCandidateError] = useState<string | null>(null);

  const handleTierChange = async (value: string) => {
    setTier(value);
    setCandidateError(null);
    setCandidates([]);
    if (!value) return;
    setLoadingCandidates(true);
    try {
      const resp = await recommendUsers(value);
      setCandidates(resp.candidates);
    } catch (err) {
      setCandidateError(err instanceof Error ? err.message : "获取推荐用户失败");
    } finally {
      setLoadingCandidates(false);
    }
  };

  const handleUseCandidate = (candidate: RecommendUserCandidateDto) => {
    // 避免重复添加同一个用户
    const exists = draft.autoCreateEntries.some((e) => e.email === candidate.email);
    if (!exists) {
      onChangeDraft({
        autoCreateEntries: [
          ...draft.autoCreateEntries,
          { account_id: candidate.account_id, email: candidate.email, username: candidate.username },
        ],
      });
    }
  };

  const handleRemoveAutoEntry = (email: string) => {
    onChangeDraft({
      autoCreateEntries: draft.autoCreateEntries.filter((e) => e.email !== email),
    });
  };

  // 场景2 manual 操作
  const handleAddManualRow = () => {
    onChangeDraft({ keyEntries: [...draft.keyEntries, emptyKeyEntry()] });
  };
  const handleRemoveManualRow = (idx: number) => {
    onChangeDraft({ keyEntries: draft.keyEntries.filter((_, i) => i !== idx) });
  };
  const handleUpdateManualRow = (idx: number, patch: Partial<KeyEntryDraft>) => {
    onChangeDraft({ keyEntries: draft.keyEntries.map((e, i) => (i === idx ? { ...e, ...patch } : e)) });
  };

  return (
    <div>
      {/* 模式切换 */}
      <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
        <label style={{ width: "auto", display: "flex", gap: 4, alignItems: "center", cursor: "pointer" }}>
          <input
            type="radio"
            name={`${testIdPrefix}-keymode`}
            checked={draft.keyMode === "auto"}
            onChange={() => onChangeDraft({ keyMode: "auto" })}
          />
          <span>推荐 Kiro 用户<span style={{ fontSize: 11, color: "#64748b", marginLeft: 4 }}>(创建时自动生成 ksk_)</span></span>
        </label>
        <label style={{ width: "auto", display: "flex", gap: 4, alignItems: "center", cursor: "pointer" }}>
          <input
            type="radio"
            name={`${testIdPrefix}-keymode`}
            checked={draft.keyMode === "manual"}
            onChange={() => onChangeDraft({ keyMode: "manual" })}
          />
          <span>手动录入 Kiro_API_Key</span>
        </label>
      </div>

      {/* 场景1：推荐用户模式 */}
      {draft.keyMode === "auto" && (
        <div>
          {/* 已选用户列表 */}
          {draft.autoCreateEntries.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>已选用户（创建时将自动为其生成 ksk_）：</div>
              {draft.autoCreateEntries.map((entry) => (
                <div key={entry.email} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}
                  data-testid={`${testIdPrefix}-auto-entry-${entry.email}`}>
                  <span style={{ fontSize: 13, fontFamily: "monospace" }}>{entry.email}</span>
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>({entry.username})</span>
                  <button type="button" style={{ fontSize: 11, padding: "1px 6px" }}
                    onClick={() => handleRemoveAutoEntry(entry.email)}>
                    移除
                  </button>
                </div>
              ))}
            </div>
          )}
          {draft.autoCreateEntries.length === 0 && (
            <p style={{ fontSize: 12, color: "#94a3b8", margin: "4px 0 8px" }}>
              请在下方推荐列表中选择用户，点击"使用"添加
            </p>
          )}
          {/* 推荐用户选择面板 */}
          <div className="form-row" style={{ marginTop: 4 }}>
            <label>按层级筛选推荐用户</label>
            <select value={tier} onChange={(e) => handleTierChange(e.target.value)}>
              <option value="">全部层级</option>
              {TIERS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          {loadingCandidates && <p style={{ fontSize: 12, color: "#64748b" }}>加载中...</p>}
          {candidateError && <span style={{ color: "#dc2626", fontSize: 12 }}>{candidateError}</span>}
          {!loadingCandidates && tier && !candidateError && (
            <ul style={{ fontSize: 12, paddingLeft: 16, marginTop: 4 }}>
              {candidates.length === 0 && <li style={{ listStyle: "none", marginLeft: -16 }}>暂无候选用户</li>}
              {candidates.map((c) => {
                const alreadySelected = draft.autoCreateEntries.some((e) => e.email === c.email);
                return (
                  <li key={c.account_id}>
                    {c.email}({c.username}){" "}
                    <button type="button" style={{ fontSize: 11, padding: "1px 6px" }}
                      onClick={() => handleUseCandidate(c)}
                      disabled={alreadySelected}>
                      {alreadySelected ? "已选" : "使用"}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* 场景2：手动录入模式 */}
      {draft.keyMode === "manual" && (
        <div>
          {draft.keyEntries.map((entry, idx) => (
            <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 4, alignItems: "center" }}
              data-testid={`${testIdPrefix}-key-row-${idx}`}>
              <input
                style={{ width: 200, fontFamily: "monospace" }}
                value={entry.key}
                onChange={(e) => handleUpdateManualRow(idx, { key: e.target.value })}
                placeholder="ksk_xxx"
              />
              <input
                style={{ width: 200 }}
                value={entry.email}
                onChange={(e) => handleUpdateManualRow(idx, { email: e.target.value })}
                placeholder="关联的 Kiro 用户邮箱"
              />
              <button type="button" onClick={() => handleRemoveManualRow(idx)} disabled={draft.keyEntries.length <= 1}>
                移除
              </button>
            </div>
          ))}
          <button type="button" onClick={handleAddManualRow}>添加 Key</button>
        </div>
      )}
    </div>
  );
}

/**
 * Kiro-Go 舰队实例创建表单,对应 design.md > 前端页面设计 `FleetCreatePage.tsx`
 * (Requirements 1.1-1.14)。支持单条模式与批量模式(≤500 条),提交后逐条
 * 展示成功/失败结果。
 */
export function FleetCreatePage() {
  const [mode, setMode] = useState<"single" | "batch">("single");

  // ---- 单条模式 ----
  const [singleDraft, setSingleDraft] = useState<RecordDraft>(emptyRecordDraft());
  const [singleErrors, setSingleErrors] = useState<string[]>([]);

  // ---- 批量模式 ----
  const [batchDrafts, setBatchDrafts] = useState<RecordDraft[]>([emptyRecordDraft()]);
  const [batchErrors, setBatchErrors] = useState<Record<number, string[]>>({});
  const [batchLimitError, setBatchLimitError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<CreateFleetInstanceResultItem[] | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleAddRow = () => {
    if (batchDrafts.length >= MAX_BATCH_RECORDS) {
      setBatchLimitError(`单次批量提交记录数量上限为 ${MAX_BATCH_RECORDS} 条`);
      return;
    }
    setBatchLimitError(null);
    setBatchDrafts((prev) => [...prev, emptyRecordDraft()]);
  };

  const handleRemoveRow = (index: number) => {
    setBatchDrafts((prev) => prev.filter((_, i) => i !== index));
    setBatchErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handleUpdateRow = (index: number, patch: Partial<RecordDraft>) => {
    setBatchDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  };

  const handleSingleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateDraft(singleDraft);
    setSingleErrors(errors);
    if (errors.length > 0) return;

    setSubmitError(null);
    setResults(null);
    setSubmitting(true);
    try {
      let resolvedKeys: { key: string; kiro_user_email: string }[] | undefined;
      if (singleDraft.keyMode === "auto") {
        setSubmitError("正在自动创建 Kiro API Key，请稍候...");
        const { keys, errors: autoErrors } = await autoCreateKeysForDraft(singleDraft.autoCreateEntries);
        if (autoErrors.length > 0) {
          setSubmitError("部分用户 Key 创建失败：\n" + autoErrors.join("\n"));
          setSubmitting(false);
          return;
        }
        resolvedKeys = keys;
        setSubmitError(null);
      }
      const resp = await createInstances([draftToRequest(singleDraft, resolvedKeys)]);
      setResults(resp.results);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "创建失败");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBatchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (batchDrafts.length === 0) {
      setBatchLimitError("请至少录入 1 条记录");
      return;
    }
    if (batchDrafts.length > MAX_BATCH_RECORDS) {
      setBatchLimitError(`单次批量提交记录数量上限为 ${MAX_BATCH_RECORDS} 条`);
      return;
    }
    setBatchLimitError(null);

    const errorsByRow: Record<number, string[]> = {};
    batchDrafts.forEach((draft, idx) => {
      const errs = validateDraft(draft);
      if (errs.length > 0) errorsByRow[idx] = errs;
    });
    setBatchErrors(errorsByRow);
    if (Object.keys(errorsByRow).length > 0) return;

    setSubmitError(null);
    setResults(null);
    const hasAutoMode = batchDrafts.some((d) => d.keyMode === "auto");
    if (hasAutoMode) {
      setSubmitError("正在自动创建 Kiro API Key，请稍候...");
    }
    setSubmitting(true);
    try {
      const requests: CreateFleetInstanceRequest[] = [];
      const allAutoErrors: string[] = [];

      for (const draft of batchDrafts) {
        if (draft.keyMode === "auto") {
          const { keys, errors: autoErrors } = await autoCreateKeysForDraft(draft.autoCreateEntries);
          if (autoErrors.length > 0) {
            allAutoErrors.push(...autoErrors);
          } else {
            requests.push(draftToRequest(draft, keys));
          }
        } else {
          requests.push(draftToRequest(draft));
        }
      }

      if (allAutoErrors.length > 0) {
        setSubmitError("部分用户 Key 创建失败，已中止批量提交：\n" + allAutoErrors.join("\n"));
        setSubmitting(false);
        return;
      }

      const resp = await createInstances(requests);
      setResults(resp.results);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "批量创建失败");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h2 className="section-title">创建 Kiro-Go 实例</h2>

      <div className="form-row">
        <label>模式</label>
        <select value={mode} onChange={(e) => setMode(e.target.value as "single" | "batch")}>
          <option value="single">单条创建</option>
          <option value="batch">批量创建</option>
        </select>
      </div>

      {mode === "single" && (
        <div className="card">
          <div className="section-title">单条创建</div>
          {singleErrors.length > 0 && (
            <div className="error-banner">
              {singleErrors.map((err, idx) => (
                <div key={idx}>{err}</div>
              ))}
            </div>
          )}
          <form onSubmit={handleSingleSubmit}>
            <div className="form-row" style={{ alignItems: "flex-start" }}>
              <label>Kiro_API_Key 录入方式</label>
              <KeyEntriesEditor
                draft={singleDraft}
                onChangeDraft={(patch) => setSingleDraft({ ...singleDraft, ...patch })}
                testIdPrefix="single"
              />
            </div>
            <div className="form-row">
              <label>Assigned_User_Email</label>
              <input
                type="email"
                value={singleDraft.assignedUserEmail}
                onChange={(e) => setSingleDraft({ ...singleDraft, assignedUserEmail: e.target.value })}
                placeholder="user@example.com"
              />
            </div>
            <div className="form-row">
              <label>到期时间</label>
              <input
                type="date"
                value={singleDraft.expiresAt}
                onChange={(e) => setSingleDraft({ ...singleDraft, expiresAt: e.target.value })}
              />
            </div>
            <div className="form-row">
              <label>名称/备注(可选,≤{MAX_NAME_NOTE_LENGTH} 字符)</label>
              <input
                value={singleDraft.nameNote}
                onChange={(e) => setSingleDraft({ ...singleDraft, nameNote: e.target.value })}
                style={{ width: 260 }}
              />
            </div>
            <div className="form-row">
              <label>部署区域</label>
              <span style={{ display: "flex", gap: 12 }}>
                {REGIONS.map((r) => (
                  <label key={r.value} style={{ width: "auto", display: "flex", gap: 4, alignItems: "center" }}>
                    <input
                      type="radio"
                      name="single-region"
                      checked={singleDraft.region === r.value}
                      onChange={() => setSingleDraft({ ...singleDraft, region: r.value })}
                    />
                    {r.label}
                  </label>
                ))}
              </span>
            </div>
            <button className="primary" type="submit" disabled={submitting}>
              {submitting ? "提交中..." : "创建"}
            </button>
          </form>
        </div>
      )}

      {mode === "batch" && (
        <div className="card">
          <div className="section-title">批量创建(≤{MAX_BATCH_RECORDS} 条)</div>
          {batchLimitError && <div className="error-banner">{batchLimitError}</div>}
          <form onSubmit={handleBatchSubmit}>
            {batchDrafts.map((draft, idx) => (
              <div
                key={idx}
                className="card"
                style={{ background: "#f8fafc", marginBottom: 8, padding: 12 }}
                data-testid={`batch-row-${idx}`}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <strong style={{ fontSize: 13 }}>第 {idx + 1} 条</strong>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => handleRemoveRow(idx)}
                    disabled={batchDrafts.length <= 1}
                  >
                    删除该行
                  </button>
                </div>
                {batchErrors[idx] && (
                  <div className="error-banner">
                    {batchErrors[idx].map((err, i) => (
                      <div key={i}>{err}</div>
                    ))}
                  </div>
                )}
                <div className="form-row" style={{ alignItems: "flex-start" }}>
                  <label>Kiro_API_Key 录入方式</label>
                  <KeyEntriesEditor
                    draft={draft}
                    onChangeDraft={(patch) => handleUpdateRow(idx, patch)}
                    testIdPrefix={`batch-${idx}`}
                  />
                </div>
                <div className="form-row">
                  <label>Assigned_User_Email</label>
                  <input
                    type="email"
                    value={draft.assignedUserEmail}
                    onChange={(e) => handleUpdateRow(idx, { assignedUserEmail: e.target.value })}
                  />
                </div>
                <div className="form-row">
                  <label>到期时间</label>
                  <input
                    type="date"
                    value={draft.expiresAt}
                    onChange={(e) => handleUpdateRow(idx, { expiresAt: e.target.value })}
                  />
                </div>
                <div className="form-row">
                  <label>名称/备注</label>
                  <input value={draft.nameNote} onChange={(e) => handleUpdateRow(idx, { nameNote: e.target.value })} />
                </div>
                <div className="form-row">
                  <label>部署区域</label>
                  <span style={{ display: "flex", gap: 12 }}>
                    {REGIONS.map((r) => (
                      <label key={r.value} style={{ width: "auto", display: "flex", gap: 4, alignItems: "center" }}>
                        <input
                          type="radio"
                          name={`batch-region-${idx}`}
                          checked={draft.region === r.value}
                          onChange={() => handleUpdateRow(idx, { region: r.value })}
                        />
                        {r.label}
                      </label>
                    ))}
                  </span>
                </div>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button type="button" onClick={handleAddRow}>
                添加一行
              </button>
              <button className="primary" type="submit" disabled={submitting}>
                {submitting ? "提交中..." : `批量创建(${batchDrafts.length} 条)`}
              </button>
            </div>
          </form>
        </div>
      )}

      {submitError && <div className="error-banner">{submitError}</div>}

      {results && (
        <div className="card">
          <div className="section-title">创建结果</div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>结果</th>
                <th>实例标识/错误</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.index}>
                  <td>{r.index + 1}</td>
                  <td>{r.success ? "成功" : "失败"}</td>
                  <td>{r.success ? r.instance_id : (r.errors ?? []).join("; ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
